/**
 * English locale catalog for the tactical GvG map sub-domain
 * (`apps/frontend/src/features/gvg-planner/components/map`).
 *
 * Each constant below is the canonical English shape; the matching Russian
 * catalog in `./ru.ts` must satisfy `typeof <enName>` exactly. Consumers pair
 * the catalog with `defineLocaleCatalog({ en, ru })` from `@/shared/i18n` and
 * resolve it through `useLocaleMessages(catalog)`.
 *
 * English is the source-of-truth shape — when a key disappears from `en`,
 * `ru` must drop the matching key or the type system will fail.
 *
 * Translation scope: the strings below are the SYSTEM-side chrome (chrome
 * around the persisted tactical map), not the user-created data that flows
 * through this surface. `MapTagObjective.label`, `MapTagObjective.tagKey`,
 * `RaidGroup.label`, and player names are persisted data and pass through
 * verbatim — they are NOT translated here.
 */
export const gvgMapEn = {
  canvas: {
    title: 'Battlefield positions',
    helpEditable:
      'The map shows Forest, Tree, and timed Boss players from their tags. Click a Forest, Tree, or Boss point to edit tagged players. Click a group to edit it, or drag a group or Tree to reposition it.',
    helpViewOnlyLeadIn: 'The map shows Forest, Tree, and timed Boss players from their tags.',
    helpViewOnly: 'Map positions are view-only.',
    officerEditBadge: 'Officer edit',
    regionLabel: 'GvG battlefield map',
    imageAlt: 'Where Winds Meet GvG battlefield map',
  },
  drawer: {
    eyebrow: 'Raid map · tag assignments',
    loading: 'Loading map plan…',
    retry: 'Retry',
    help: 'Forest, Tree, and timed Boss markers show players with their matching live tags. Officers can click a marker to select its tagged players. Drag group circles or the Tree to save their positions.',
    emptyGroups: 'This raid has no Attack or Defense groups to place on the map yet.',
    titleFallback: 'GvG map plan',
    titleForRaid: (raidName: string) => `${raidName} · Map plan`,
  },
  groupMarker: {
    /**
     * Position descriptor used in the aria-label after the roster. The
     * percentages stay as raw numbers — they're read naturally as
     * "nineteen percent from left" / "thirty-eight percent from top".
     */
    positionPercent: (xPercent: number, yPercent: number) =>
      `Map position ${xPercent} percent from left, ${yPercent} percent from top.`,
    editPrompt:
      ' Click to edit the group roster. Drag or use arrow keys to move.',
    fallbackTitle: 'No assigned players',
    fallbackLabel: (markerNumber: string) => `Group ${markerNumber}`,
  },
  tagMarker: {
    /**
     * Tag-marker aria-label. The objective label (`Tree`, `15 min boss`,
     * `forest top ally near`) is persisted data and is interpolated raw.
     */
    rosterLabel: (objectiveLabel: string, playerCount: number) =>
      `${objectiveLabel}: ${playerCount} ${playerCount === 1 ? 'player' : 'players'}. Focus to view the roster.`,
    editPlayersPrompt: ' Click to edit the tagged players.',
    editAndMovePrompt: ' Click to edit the tagged players. Drag or use arrow keys to move.',
    movableAction: 'Click to edit · drag to move',
    staticAction: 'Click to edit players',
  },
  popover: {
    movableIconTitle: 'Movable map marker',
    /** Default empty roster fallback when no `emptyMessage` prop is supplied. */
    emptyRoster: 'No assigned players',
    playerCountSingular: (count: number) => `${count} player`,
    playerCountPlural: (count: number) => `${count} players`,
  },
}
